import { toggleNeedScroll } from 'client/actions/actionsManagement'
import CreateBlockDialog from 'client/components/core/CreateBlockDialog'
import { CreateEntityProvider } from 'client/context/CreateEntityContext'
import { useDeviceModeContext } from 'client/context/DeviceModeContext'
import { ResizingRowProvider } from 'client/context/ResizingRowContext'
import useManagement from 'client/hooks/useManagement'
import usePresentPage from 'client/hooks/usePresentPage'
import { createPage } from 'client/pages'
import {
  getEditingId,
  getNeedScroll,
  isCreateBlockDialogActive,
} from 'client/reducers/managementReducer'
import {
  getAllPageEntities,
  getPageType,
  getRootEntity,
  getTracking,
} from 'client/reducers/pageReducer'
import { pageSelectors, usePage } from 'client/store'
import managementSelectors from 'client/store/management/managementSelectors'
import AffiliateBadge from 'common/components/entities/AffiliateBadge'
import { useActions } from 'common/hooks/useActions'
import { getFilteredEntitiesIdsByPageType } from 'common/utils/entityUtils'
import GlobalStyles from 'common/utils/GlobalStyles'
import React, { memo, useEffect, useRef, useState } from 'react'
import { useTrackingStyles } from 'src/editor/hooks/use-tracking-styles'
import extractCssFromHtml from 'src/editor/shared/utils/extract-css-from-html'
import useCustomFontFaces from '../../../hooks/useCustomFontFaces'
import usePageFonts from '../../../hooks/usePageFonts'
import PageUi from './ui/PageUi'

function Page() {
  const pageRef = useRef()
  const { isMobile } = useDeviceModeContext()
  const isDialogActive = useManagement(isCreateBlockDialogActive)
  const isUserFreemium = useManagement(managementSelectors.isUserFreemium)
  const needScroll = useManagement(getNeedScroll)
  const pageType = usePresentPage(getPageType)
  const rootEntity = usePresentPage(getRootEntity)
  const dispatchToggleNeedScroll = useActions(toggleNeedScroll)
  const editingEntity = useManagement(getEditingId)
  const pageEntities = usePresentPage(getAllPageEntities)
  const tracking = usePresentPage(getTracking)
  const excludedEntitiesIds = getFilteredEntitiesIdsByPageType(
    pageEntities,
    pageType,
  )
  const globalScrollPaddingTop = usePage(
    pageSelectors.getGlobalScrollPaddingTop,
  )

  const [trackingCss, setTrackingCss] = useState('')

  useTrackingStyles(trackingCss)
  usePageFonts()
  useCustomFontFaces()

  useEffect(() => {
    if (needScroll) {
      pageRef.current.scrollTop = pageRef.current.scrollHeight
      dispatchToggleNeedScroll(false)
    }
  })

  useEffect(() => {
    const editingElement = document.getElementById(editingEntity)
    if (editingElement) {
      try {
        // don't scroll if in a view already but not centered
        editingElement.scrollIntoViewIfNeeded(true)
      } catch {
        // because scrollIntoViewIfNeeded is experimental
        editingElement.scrollIntoView({
          behavior: 'auto',
          block: 'center',
          inline: 'center',
        })
      }
    }
  }, [editingEntity, isMobile])

  useEffect(() => {
    if (tracking) {
      const headerCss = extractCssFromHtml(tracking.headerCode || '')
      const footerCss = extractCssFromHtml(tracking.footerCode || '')
      setTrackingCss(headerCss + footerCss)
    }
  }, [tracking])

  const { PageComponent, createEntityElement } = createPage(pageType)

  const showAffiliateBadge = Boolean(
    rootEntity?.options?.affiliateBadge ||
      rootEntity?.affiliateBadge ||
      rootEntity?.isAffiliateBadgeVisible ||
      isUserFreemium,
  )

  return (
    //do not delete id="page-ui", it is needed for tiptap toolbar to work.
    <PageUi ref={pageRef} id="page-ui">
      <GlobalStyles scrollPaddingTop={globalScrollPaddingTop} />
      <CreateEntityProvider
        excludedEntitiesIds={excludedEntitiesIds}
        createEntityElement={createEntityElement}
      >
        <PageComponent>
          <ResizingRowProvider>
            {createEntityElement(rootEntity)}
          </ResizingRowProvider>
          {isDialogActive && <CreateBlockDialog />}
        </PageComponent>
      </CreateEntityProvider>
      {showAffiliateBadge && <AffiliateBadge />}
    </PageUi>
  )
}

export default memo(Page)
